import React, { useRef } from 'react';
import { Typography } from 'housecall-ui';
import Button from 'housecall-ui/dist/DesignSystem/Inputs/Buttons/Button';

import { Photo } from '../../state/types';
import { useOnlineBookingReducer } from '../../state/useOnlineBookingReducer';
import { useToastController } from '../../toast';
import { Nullable } from '../../types/types';

import { IMAGE_ACCEPTED_TYPES, IMAGE_LIMIT, IMAGE_MAX_SIZE, IMAGE_TYPE_INPUT } from './consts';

export const ERROR_SIZE_AND_DUPLICATE = 'Photos not uploaded due to the 10MB size limit or duplicates';
export const ERROR_SIZE = "Some photos exceeded the 10MB limit and weren't uploaded.";
export const ERROR_DUPLICATE = "Some photos weren't uploaded; they were duplicates.";

const filterOutFiles = (files: Nullable<FileList>): { files: File[]; exceededSize: boolean } => {
  let exceededSize = false;

  if (!files || files.length === 0) {
    return { files: [], exceededSize };
  }

  const filteredFiles = Array.from(files).filter((file) => {
    if (IMAGE_ACCEPTED_TYPES.includes(file?.type) && file?.size <= IMAGE_MAX_SIZE) {
      return true;
    }
    exceededSize = true;
    return false;
  });

  return {
    files: filteredFiles,
    exceededSize,
  };
};

const removeDuplicated = (files: File[], addedPhotos: Photo[]): { files: File[]; hasDuplicates: boolean } => {
  const names = files.map((file) => file.name);
  const addedPhotosNames = addedPhotos.map(({ file }) => file.name);

  let hasDuplicates = false;
  const uniqueNames = [...new Set(names)];
  uniqueNames.map((name) => files.find((file) => file.name === name));

  hasDuplicates = uniqueNames.length !== names.length;

  if (uniqueNames.some((name) => addedPhotosNames.includes(name))) {
    hasDuplicates = true;
  }

  return {
    files: uniqueNames
      .filter((name) => !addedPhotosNames.includes(name))
      .map((name) => files.find((file) => file.name === name)) as File[],
    hasDuplicates,
  };
};

type Props = {
  onChange: (photos: File[]) => void;
  photos: Photo[];
};

const generateToastCopy = (exceededSize: boolean, hasDuplicates: boolean) => {
  if (exceededSize && hasDuplicates) {
    return ERROR_SIZE_AND_DUPLICATE;
  }
  if (exceededSize && !hasDuplicates) {
    return ERROR_SIZE;
  }
  if (hasDuplicates && !exceededSize) {
    return ERROR_DUPLICATE;
  }
};

const UploadPhotosInput = ({ onChange, photos: addedPhotos }: Props) => {
  const { state } = useOnlineBookingReducer();
  const inputRef = useRef<HTMLInputElement>(null);
  const { showToast } = useToastController();
  const { cart } = state;
  const photosCount = cart.reduce((prev, curr) => prev + (curr?.photos?.length || 0), 0);

  const handleAddAttachments = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const addedFiles = event.target.files;
    const { files: photos, exceededSize } = filterOutFiles(addedFiles);

    const { files, hasDuplicates } = removeDuplicated(photos, addedPhotos);

    const toastMessage = generateToastCopy(exceededSize, hasDuplicates);
    if (toastMessage) {
      showToast(toastMessage);
    }

    const photosLimited: File[] = files.slice(0, IMAGE_LIMIT - photosCount);
    if (photosLimited) {
      onChange(photosLimited);
    }

    if (inputRef?.current) {
      inputRef.current.value = '';
    }
  };

  return (
    <>
      <Typography variant="body1" gutterBottom>
        Add pictures to make your description more detailed.
      </Typography>
      <Button
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        component="label"
        version="v2"
        color="secondary"
        variant="outlined"
      >
        Upload photos
        <input
          ref={inputRef}
          type="file"
          accept={IMAGE_TYPE_INPUT}
          multiple
          data-testid="attachment-upload"
          hidden
          onChange={handleAddAttachments}
        />
      </Button>
    </>
  );
};

export default UploadPhotosInput;
